// Library methods
import { useCallback, useMemo } from "react";

// Components
import Skeleton from "@mui/material/Skeleton";
import GPACross from "src/assets/shapes/GPACross/GPACross";

// Constants
import { GPA_CHART_COLORS } from "src/constants/enums";

type GPAChartType = {
  gpaPoints: Array<{ x: number; y: number; kind: string; symbol: string }>;
  containerSize?: number;
};

const DEFAULT_CONTAINER_SIZE = 312;

const GPAChart = ({
  gpaPoints,
  containerSize = DEFAULT_CONTAINER_SIZE,
}: GPAChartType) => {
  const size = useMemo(() => containerSize * 0.9, [containerSize]);
  const tickSize = useMemo(() => size / 104, [size]);
  const scaleFactor = useMemo(() => size / DEFAULT_CONTAINER_SIZE, [size]);

  const calculateXPaddingNumberGraph = useCallback((xFloor: number) => {
    // jumpAxis lets us jump from negative x axis to positive x axis (half of total width + line width + padding)
    const jumpAxis = 137;

    if (xFloor <= 0) {
      return 163 - xFloor * -31;
    } else {
      return xFloor * 31 + jumpAxis;
    }
  }, []);

  const calculateYPaddingNumberGraph = useCallback((yFloor: number) => {
    // jumpAxis lets us jump from negative y axis to negative y axis (half of total width + line width + padding)
    const jumpAxis = 135;

    if (yFloor >= 0) {
      return 160 - yFloor * 29;
    } else {
      return jumpAxis + yFloor * -29;
    }
  }, []);

  const getPaddingLeft = useCallback(
    (ml: number) => {
      return ml * scaleFactor;
    },
    [scaleFactor]
  );

  const getPaddingTop = useCallback(
    (mt: number) => {
      const value = mt + 6;
      return value * scaleFactor;
    },
    [scaleFactor]
  );

  const getPointColor = useCallback(
    (point: { x: number; y: number; kind: string; symbol: string }) => {
      if (point.kind === "dot") {
        return GPA_CHART_COLORS.unflagged;
      }
      switch (point.symbol) {
        case "△":
        case "\u25b3": // triangle
          return GPA_CHART_COLORS.flagged_once;
        case "◇":
        case "\u25c7": // diamond
        case "\u25ed": // small diamond
          return GPA_CHART_COLORS.flagged_twice;
        case "▲":
        case "\u25b2": // up triangle
          return GPA_CHART_COLORS.flagged_multiple;
        case "X":
          return GPA_CHART_COLORS.deep_loss;
        default:
          return "#000000"; // black fallback
      }
    },
    []
  );

  if (!gpaPoints || gpaPoints.length === 0) {
    return (
      <Skeleton
        variant="rounded"
        width="100%"
        height={DEFAULT_CONTAINER_SIZE}
        sx={{
          borderRadius: "4px",
        }}
      />
    );
  }

  return (
    <main
      className="retinalogik-analysis-report"
      style={{
        height: `${size}px`,
        width: `${size}px`,
        position: "relative",
      }}
    >
      <GPACross width={size} height={size} tickSize={tickSize} />

      {gpaPoints.map(
        (
          point: { x: number; y: number; kind: string; symbol: string },
          i: number
        ) => {
          const ml = calculateXPaddingNumberGraph(point.x);
          const mt = calculateYPaddingNumberGraph(point.y);
          const pointColor = getPointColor(point);

          return (
            <div
              key={i}
              style={{
                position: "absolute",
                top: getPaddingTop(mt),
                left: getPaddingLeft(ml),
                transform: "translate(-50%, -50%)",
                color: pointColor,
              }}
            >
              <div
                style={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  width: "14px",
                  height: "14px",
                }}
              >
                {point.kind === "dot" ? (
                  <div
                    style={{
                      backgroundColor: pointColor,
                      border: `1px solid ${pointColor}`,
                      width: "4px",
                      height: "4px",
                      borderRadius: "50%",
                      MozBorderRadius: "50%",
                      WebkitBorderRadius: "50%",
                    }}
                  />
                ) : point.kind === "triangle1" ? (
                  <div
                    style={{
                      fontSize: "12px",
                    }}
                  >
                    {point.symbol}
                  </div>
                ) : point.kind === "triangle2" ? (
                  <div
                    style={{
                      fontSize: "16px",
                    }}
                  >
                    {point.symbol}
                  </div>
                ) : point.kind === "triangle3" ? (
                  <div
                    style={{
                      fontSize: "14px",
                    }}
                  >
                    {point.symbol}
                  </div>
                ) : (
                  <div
                    style={{
                      fontSize: "12px",
                    }}
                  >
                    {point.symbol}
                  </div>
                )}
              </div>
            </div>
          );
        }
      )}
    </main>
  );
};

export default GPAChart;
